import { useEffect, useState } from "react";
import { getStorageDownloadURL } from "@/components/Firebase/firebase";

// Module-level cache: a Storage path resolves to the same tokenized URL for the
// session, so repeated tiles/re-renders never refetch the same document.
const urlCache = new Map<string, string>();

const isHttp = (value: string): boolean => /^https?:\/\//i.test(value);

export interface DownloadUrlState {
  url: string | null;
  loading: boolean;
  error: boolean;
}

// Resolves a Firebase Storage path (e.g. "userData/{uid}/...") to a download
// URL. Already-resolved http(s) URLs pass through unchanged; no path yields
// nulls. Used to preview on-file KYC documents.
export function useDownloadUrl(path?: string | null): DownloadUrlState {
  const [url, setUrl] = useState<string | null>(() => {
    if (!path) return null;
    return isHttp(path) ? path : urlCache.get(path) || null;
  });
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    if (!path) {
      setUrl(null);
      return;
    }
    if (isHttp(path)) {
      setUrl(path);
      return;
    }
    const hit = urlCache.get(path);
    if (hit) {
      setUrl(hit);
      return;
    }
    setUrl(null);
    let active = true;
    // Wrap resolution so a single bad/missing document degrades to "no preview"
    // instead of crashing the wizard.
    (async () => {
      try {
        const resolved: string = await getStorageDownloadURL(path);
        if (!active) return;
        urlCache.set(path, resolved);
        setUrl(resolved);
      } catch {
        if (active) setError(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [path]);

  return { url, loading: Boolean(path) && !url && !error, error };
}
